import { Injectable, inject } from "@angular/core";

import
{
    AggregateCriteria,
    ExecutionResult,
    IAppRegistrationModel,
    IChartCriteria,
    IPhoto,
    KeyValuePair,
    PhotoViewModel,
    ProfileSettingViewModel,
    ProfileViewModel,
} from "@tylertech/tyler-api-types";

import { ApiService } from "./apiService";

@Injectable()
export class UserProfileService
{
    private readonly $apiService = inject(ApiService);


    Clear(profile: ProfileViewModel): Promise<ProfileViewModel>
    {
        return this.$apiService.sendAuth("IUserProfileService/Clear", profile);
    }

    Get(): Promise<ExecutionResult<ProfileViewModel>>
    {
        return this.$apiService.sendAuth("IUserProfileService/Get");
    }

    GetFavoriteApps(): Promise<KeyValuePair<string, string>[]>
    {
        return this.$apiService.sendAuth("IUserProfileService/GetFavoriteApps");
    }

    GetFormShortcuts(formId: string): Promise<string[]>
    {
        return this.$apiService.sendAuth("IUserProfileService/GetFormShortcuts", formId);
    }

    GetPhoto(): Promise<PhotoViewModel>
    {
        return this.$apiService.sendAuth("IUserProfileService/GetPhoto");
    }

    GetRecentApps(module: string): Promise<KeyValuePair<string, string>[]>
    {
        return this.$apiService.sendAuth("IUserProfileService/GetRecentApps", module);
    }

    GetUserLookupType(): Promise<string>
    {
        return this.$apiService.sendAuth("IUserProfileService/GetUserLookupType");
    }

    HasPhoto(): Promise<boolean>
    {
        return this.$apiService.sendAuth("IUserProfileService/HasPhoto");
    }

    RemoveFavoriteApp(favorite: KeyValuePair<string, string>): Promise<ExecutionResult<boolean>>
    {
        return this.$apiService.sendAuth("IUserProfileService/RemoveFavoriteApp", favorite);
    }

    Save(profile: ProfileViewModel): Promise<ExecutionResult<boolean>>
    {
        return this.$apiService.sendAuth("IUserProfileService/Save", profile);
    }

    SaveFavoriteApp(favorite: KeyValuePair<string, string>): Promise<ExecutionResult<boolean>>
    {
        return this.$apiService.sendAuth("IUserProfileService/SaveFavoriteApp", favorite);
    }

    SavePhoto(photo: IPhoto): Promise<boolean>
    {
        return this.$apiService.sendAuth("IUserProfileService/SavePhoto", photo);
    }

    SaveRecent(app: IAppRegistrationModel): Promise<ExecutionResult<boolean>>
    {
        return this.$apiService.sendAuth("IUserProfileService/SaveRecent", app);
    }

    SetPositions(groups: Array<Array<IChartCriteria | AggregateCriteria>>): Promise<Array<ProfileSettingViewModel>>
    {
        const settings: Array<ProfileSettingViewModel> = [];
        const charCodeA = "A".charCodeAt(0);

        for (let i = 0; i < groups.length; i++)
        {
            const segment = groups[i];
            const columnCharacter = String.fromCharCode(charCodeA + i);
            for (let j = 0; j < segment.length; j++)
            {
                const item = segment[j];
                const pos = item.Parameters.filter(v => v.Caption === "Position")[0];
                if (!pos)
                {
                    console.error(`The provider ${item.ProviderId} does not have a position parameter.`);
                    continue;
                }
                if (i === groups.length - 1)
                    pos.Value = "X-" + pad(j, 3);
                else
                    pos.Value = columnCharacter + "-" + pad(j, 3);
                settings.push(pos);
            }
        }

        return this.updateUserProfile(settings);
    }

    SetHidden(aggregateCriteria: AggregateCriteria): Promise<Array<ProfileSettingViewModel>>
    {
        const positionSetting = aggregateCriteria.Parameters.find(v => v.Caption === "Position");
        if (!positionSetting)
            return Promise.resolve(null);
        positionSetting.Value = "X-" + positionSetting.Value.toString().split("-")[1];
        return this.updateUserProfile([positionSetting]);
    }

    private updateUserProfile(settings: Array<ProfileSettingViewModel>): Promise<Array<ProfileSettingViewModel>>
    {
        return this.Get()
            .then(executionResult =>
            {
                executionResult.Result.Settings = settings;

                // TODO - this is a disconnected promise... should we refactor it to return `pars` after the save is completed?
                this.Save(executionResult.Result);
                return settings;
            });
    }

}

function pad(val: number, size: number): string
{
    const sign = val < 0 ? "-" : "";
    return sign + new Array(size)
        .concat([Math.abs(val)])
        .join("0")
        .slice(-size);
}
